import DateTimePicker from '@/components/Form/DateTimePicker'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { startTicketByID } from '@/modules/Ticket/services'
import { Ticket } from '@/modules/Ticket/types/response'
import useAuthStore from '@/stores/useAuthStore'
import { ModalProps } from '@/types/shared'
import { formatFromUTC } from '@/utils/function'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, Typogaphy } from 'vnetwork-ui'
import { DEPARTMENT_IDS } from '@/constants/departments'

export interface StartTicketConfirmProps extends ModalProps {
    ticket: Ticket
    onSuccess?: () => void
}

interface StartTicketFormData {
    ticket_time_response: dayjs.Dayjs | null
    ticket_technical_assignees: number[]
    reason: string
    override_assignees: boolean
}

const StartTicketConfirm: React.FC<StartTicketConfirmProps> = ({
    onClose,
    open,
    ticket,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { employee } = useAuthStore()
    const { refetchAll: refetchLogs } = useDataGridRefetch({
        reactQueryKey: `ticket-logs-${ticket?.ticket_id}`,
    })

    const hasExistingAssignees =
        Array.isArray(ticket?.ticket_technical_assignees) &&
        ticket.ticket_technical_assignees.length > 0

    // Auto-fill current employee ID when no existing assignees
    const getDefaultAssignees = () => {
        if (hasExistingAssignees) {
            return ticket?.ticket_technical_assignees || []
        }
        // Auto-fill with current employee ID
        return employee?.employee_id ? [employee.employee_id] : []
    }

    const { control, watch, reset, handleSubmit } =
        useForm<StartTicketFormData>({
            defaultValues: {
                ticket_time_response: dayjs(),
                ticket_technical_assignees: getDefaultAssignees(),
                reason: '',
                override_assignees: false,
            },
        })

    const overrideAssignees = watch('override_assignees')

    const { mutateAsync: startTicketMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: startTicketByID },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('ticket-start-success'),
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.TICKET_DETAIL, ticket?.ticket_id],
                    })
                    refetchLogs()
                    onSuccess?.()
                    handleClose()
                },
            }
        )

    const handleClose = () => {
        reset()
        onClose()
    }

    const onSubmit = async (data: StartTicketFormData) => {
        await startTicketMutation({
            ticket_id: ticket?.ticket_id || 0,
            ticket_time_response: data.ticket_time_response
                ? data.ticket_time_response.toISOString()
                : undefined,
            ticket_technical_assignees:
                overrideAssignees || !hasExistingAssignees
                    ? data.ticket_technical_assignees
                    : undefined,
            reason: data.reason || undefined,
        })
    }

    // Calculate time difference between planned and actual
    const plannedStartTime = ticket?.ticket_time_repply
    const actualStartTime = watch('ticket_time_response')
    // Parse planned time as UTC then convert to local to avoid timezone drift
    const parsedPlannedStart = plannedStartTime
        ? dayjs.utc(plannedStartTime).tz()
        : null

    const timeDiff =
        parsedPlannedStart && actualStartTime
            ? actualStartTime.diff(parsedPlannedStart, 'minute')
            : null

    return (
        <Dialog
            onClose={handleClose}
            open={open}
            title={t('ticket-start-confirm-title')}
            children={
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* Ticket Info */}
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between">
                            <Typogaphy size="small" className="text-gray-500">
                                Ticket ID:
                            </Typogaphy>
                            <Typogaphy size="small" className="font-medium">
                                DC Ticket - {ticket?.ticket_id}
                            </Typogaphy>
                        </div>
                        <div className="flex justify-between">
                            <Typogaphy size="small" className="text-gray-500">
                                {t('ticket-time-repply')}:
                            </Typogaphy>
                            <Typogaphy size="small" className="font-medium">
                                {formatFromUTC(
                                    ticket?.ticket_time_repply || ''
                                )}
                            </Typogaphy>
                        </div>
                    </div>

                    {/* Time Response */}
                    <div>
                        <DateTimePicker
                            control={control}
                            name="ticket_time_response"
                            label={t('ticket-time-repply')}
                            required
                        />
                        {timeDiff !== null && timeDiff !== 0 && (
                            <Typogaphy
                                size="small"
                                className={`mt-1 ${timeDiff > 0 ? 'text-orange-600' : 'text-green-600'}`}
                            >
                                {timeDiff > 0
                                    ? t('ticket-start-late', {
                                          minutes: Math.abs(timeDiff),
                                      })
                                    : t('ticket-start-early', {
                                          minutes: Math.abs(timeDiff),
                                      })}
                            </Typogaphy>
                        )}
                    </div>

                    {/* Override Assignees Section */}
                    {hasExistingAssignees && (
                        <div className="border rounded-lg p-3 space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Typogaphy
                                        size="small"
                                        className="font-medium"
                                    >
                                        {t('ticket-current-assignees')}:
                                    </Typogaphy>
                                    <Typogaphy
                                        size="small"
                                        className="text-gray-600"
                                    >
                                        {ticket?.ticket_technical_assignee_names
                                            ?.map((item) => item.employee_name)
                                            .join(', ') || '-'}
                                    </Typogaphy>
                                </div>
                            </div>

                            <FormItem
                                control={control}
                                name="override_assignees"
                                type="checkbox"
                                label={t('ticket-override-assignees')}
                            />

                            {overrideAssignees && (
                                <EmployeeSelect
                                    label={t('ticket-new-assignees')}
                                    control={control}
                                    name="ticket_technical_assignees"
                                    multiple
                                    onlyActive
                                    departmentIds={[
                                        DEPARTMENT_IDS.TECHNOLOGY,
                                        DEPARTMENT_IDS.DEVELOPMENT,
                                        DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                                    ]}
                                />
                            )}
                        </div>
                    )}

                    {/* If no existing assignees, show employee select */}
                    {!hasExistingAssignees && (
                        <EmployeeSelect
                            label={t('ticket-technical-assignees')}
                            control={control}
                            name="ticket_technical_assignees"
                            multiple
                            onlyActive
                            departmentIds={[
                                DEPARTMENT_IDS.TECHNOLOGY,
                                DEPARTMENT_IDS.DEVELOPMENT,
                                DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                            ]}
                        />
                    )}
                </form>
            }
            actions={
                <div className="flex items-center gap-2">
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        loading={isPending}
                        disabled={isPending}
                    >
                        {t('ticket-start-processing')}
                    </Button>
                </div>
            }
        />
    )
}

export default StartTicketConfirm
